import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface HeroDigit {
  value: string;
  suffix?: string;
  label: string;
}

interface HeroProps {
  title: string;
  description: ReactNode;
  digits?: HeroDigit[];
  backgroundImage?: string;
  formTitle?: string;
  formDescription?: string;
  headline?: string;
}

export const Hero = ({
  title,
  description,
  digits: _digits = [],
  backgroundImage = '/images/hero-8.jpg',
  formTitle: _formTitle = 'Get instant access to case study + demo video',
  formDescription: _ = 'Instant PDF download + pre-recorded demo video access',
  headline
}: HeroProps) => {
  return (
    <>
      {/* MOBILE-ONLY HERO (demo-21 style) */}
      <section
        id="hero-21-mobile"
        className="hero-section relative md:max-lg:!mt-[80px] z-[3] pt-[100px] xsm:max-sm:pt-[80px] !pb-[60px] lg:max-xl:pt-[150px] lg:max-xl:!pb-[50px] md:max-lg:pt-[80px] md:max-lg:!pb-[40px] md:hidden bg-white"
      >
        <div className="container !text-center">
          {/* HERO TEXT */}
          <div className="flex flex-wrap mx-[calc(-0.5*_1.5rem)] justify-center">
            <div className="md:w-10/12 lg:max-xl:w-9/12 xl:w-9/12 w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
              <div className="hero-21-txt wow fadeInUp mb--60 !mb-[60px] lg:max-xl:!mb-[50px] md:max-lg:!mb-[40px] xsm:max-sm:!mb-[30px]">
                {headline ? (
                  <p
                    className="text-xs font-semibold uppercase tracking-wide mb-3 text-left md:text-center"
                    style={{ color: '#353f4f' }}
                  >
                    ✨ {headline}
                  </p>
                ) : null}
                {/* Title */}
                <h2
                  className="s-56 w--700 xl:!text-[3.5rem] md:max-lg:!text-[2.94117rem] lg:max-xl:!text-[3.25rem] sm:max-md:!text-[2.375rem] xsm:max-sm:!text-[2.0625rem] !font-bold leading-[1.25] font-Jakarta sm:max-md:!leading-[1.35] xsm:max-sm:!leading-[1.35] xl:!mb-[35px] lg:max-xl:!mb-[25px] md:max-lg:!mb-[20px] md:max-lg:px-[3%] md:max-lg:py-0 sm:max-md:!mb-[20px] xsm:max-sm:!mb-[18px]"
                  style={{ color: '#353f4f' }}
                >
                  {title}
                </h2>
                <div
                  className="text-left md:text-center space-y-3 mb-6 max-w-2xl mx-auto"
                  style={{ color: '#353f4f' }}
                >
                  {typeof description === 'string' ? (
                    <p className="text-base leading-relaxed !mb-0">
                      {description}
                    </p>
                  ) : (
                    description
                  )}
                </div>
                {/* Buttons */}
                <div className="btns-group inline-block">
                  <Link
                    to="/pricing"
                    className="btn !rounded-[4px] btn--blue-400 hover--blue-500 mr-[15px] lg:max-xl:mr-[12px] md:max-lg:mr-[10px]"
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/demo"
                    className="btn !rounded-[4px] btn--tra-black hover--theme"
                  >
                    Watch demo
                  </Link>
                </div>
                {/* Advantages List */}
                <ul className="advantages ico-15 clearfix xl:!mt-[20px] lg:max-xl:!mt-[15px] md:max-lg:!mt-[15px] sm:max-md:!mt-[15px] xsm:max-sm:!mt-[15px]">
                  <li className="w-auto inline-block align-top clear-none xsm:max-sm:!block xsm:max-sm:!mt-[4px]">
                    <p
                      className="p-sm inline-block float-left !mb-0 xsm:max-sm:block xsm:max-sm:float-none"
                      style={{ color: '#353f4f' }}
                    >
                      Available in Ontario &amp; Alberta
                    </p>
                  </li>
                  {/* <li className="advantages-links-divider w-auto inline-block align-top clear-none xsm:max-sm:!hidden">
                    <p className="p-sm inline-block float-left !mb-0 xsm:max-sm:block xsm:max-sm:float-none" style={{ color: '#353f4f' }}>
                      <span className="flaticon-minus"></span>
                    </p>
                  </li>
                  <li className="w-auto inline-block align-top clear-none xsm:max-sm:!block xsm:max-sm:!mt-[4px]">
                    <p className="p-sm inline-block float-left !mb-0 xsm:max-sm:block xsm:max-sm:float-none" style={{ color: '#353f4f' }}>
                      Limited availability
                    </p>
                  </li> */}
                </ul>
              </div>
            </div>
          </div>
          {/* END HERO TEXT */}

          {/* HERO IMAGE/VIDEO */}
          <div className="flex flex-wrap mx-[calc(-0.5*_1.5rem)] mt-[40px] xsm:max-sm:mt-[30px]">
            <div className="flex-[0_0_auto] w-full max-w-full px-[calc(0.5*_1.5rem)]">
              <div className="hero-21-img video-preview wow fadeInUp relative">
                {/* Play Icon */}
                <a
                  className="video-popup1"
                  href="https://www.youtube.com/embed/SZEflIVnhH8"
                >
                  <div className="video-btn video-btn-xl top-[calc(50%_-_2px)] !w-[5rem] !h-[5rem] mt-[-2.5rem] ml-[-2.5rem] !absolute inline-block !text-center !text-white rounded-[100%] left-2/4 before:content-[''] before:absolute before:left-[-5px] before:right-[-5px] before:top-[-5px] before:bottom-[-5px] before:opacity-0 before:transition-all before:duration-[400ms] before:ease-[ease-in-out] before:rounded-[50%] before:bg-[rgba(255,255,255,0.2)] group hover:before:opacity-75 hover:before:left-[-1.5rem] hover:before:right-[-1.5rem] hover:before:top-[-1.5rem] hover:before:bottom-[-1.5rem] bg--theme">
                    <div className="video-block-wrapper transition-all duration-[400ms] ease-[ease-in-out] group-hover:scale-95">
                      <span className="flaticon-play-button"></span>
                    </div>
                  </div>
                </a>
                {/* Preview Image */}
                <img
                  className="img-fluid inline-block w-full"
                  src="/images/tablet-01.png"
                  alt="video-preview"
                />
              </div>
            </div>
          </div>
        </div>
        {/* End container */}
      </section>
      {/* END MOBILE-ONLY HERO */}

      {/* DESKTOP HERO (existing hero-8) */}
      <section
        id="hero-8"
        className="bg--fixed hero-section relative md:max-lg:!mt-[80px] z-[3] pt-[170px] !pb-[30px] lg:max-xl:pt-[150px] lg:max-xl:!pb-[20px] md:max-lg:pt-[80px] md:max-lg:!pb-[30px] bg-scroll sm:max-md:w-auto xsm:max-sm:w-auto w-full bg-no-repeat bg-[center_center] bg-cover hidden md:block"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="container">
          <div className="flex flex-wrap mx-[calc(-0.5*_1.5rem)] items-center">
            {/* HERO TEXT */}
            <div className="md:w-6/12 lg:max-xl:w-7/12 xl:w-7/12 w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
              <div className="hero-8-txt color--white wow fadeInRight mt-[-70px] lg:max-xl:!mt-[-50px] md:max-lg:!mt-[-30px] xsm:max-sm:!p-[0_5%]">
                {/* Headline */}
                {headline && (
                  <p className="p-md color--white xl:!text-[1.125rem] lg:max-xl:!text-[1.0625rem] md:max-lg:!text-[1.0625rem] sm:max-md:!text-[1rem] xsm:max-sm:!text-[0.9375rem] !font-semibold !mb-[12px] lg:max-xl:!mb-[10px] md:max-lg:!mb-[8px] sm:max-md:!mb-[8px] xsm:max-sm:!mb-[8px] uppercase tracking-[0.5px]">
                    ✨ {headline}
                  </p>
                )}
                {/* Title */}
                <h2 className="s-54 w--700 xl:!text-[3.375rem] lg:max-xl:!text-[3.125rem] md:max-lg:!text-[2.79411rem] sm:max-md:!text-[2.375rem] xsm:max-sm:!text-[2.0625rem] !font-bold leading-[1.25] font-Jakarta sm:max-md:!leading-[1.35] xsm:max-sm:!leading-[1.35] xl:!mb-[28px] lg:max-xl:!mb-[24px] md:max-lg:!mb-[18px] sm:max-md:!mb-[15px] xsm:max-sm:!mb-[18px]">
                  {title}
                </h2>

                {/* Text */}
                <div className="p-lg color--white pr-[10%] md:max-lg:pr-0 xl:!mb-[32px] lg:max-xl:!mb-[28px] md:max-lg:!mb-[24px] sm:max-md:!mb-[25px] xsm:max-sm:!mb-[25px] space-y-3 [&_strong]:text-white">
                  {typeof description === 'string' ? (
                    <p className="!mb-0">{description}</p>
                  ) : (
                    description
                  )}
                </div>

                {/* HERO BUTTONS */}
                <div className="btns-group inline-block pr-[15%] md:max-lg:pr-0 xl:!mb-[32px] lg:max-xl:!mb-[28px] md:max-lg:!mb-[24px]">
                  <Link
                    to="/pricing"
                    className="btn !rounded-[4px] btn--blue-400 hover--blue-500 mr-[15px] lg:max-xl:mr-[12px] md:max-lg:mr-[10px]"
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/demo"
                    className="btn r-04 !rounded-[4px] btn--tra-white hover--blue-500"
                  >
                    Watch demo
                  </Link>
                </div>
              </div>
            </div>
            {/* END HERO TEXT */}

            {/* HERO IMAGE */}
            <div className="md:w-6/12 lg:max-xl:w-5/12 xl:w-5/12 w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
              <div className="hero-3-img wow fadeInLeft relative !text-center mr-[-90%] ml-[-60px] my-0 lg:max-xl:mr-[-75%] lg:max-xl:ml-0 lg:max-xl:my-0 md:max-lg:mr-[-90%] md:max-lg:ml-[10px] md:max-lg:my-0 xsm:max-sm:!mt-[35px]">
                <img
                  className="img-fluid inline-block"
                  src="/images/tablet-01.png"
                  alt="hero-image"
                />
              </div>
            </div>
            {/* END HERO IMAGE */}
          </div>
          {/* End row */}
        </div>
        {/* End container */}

        {/* WAVE SHAPE BOTTOM */}
        <div className="wave-shape-bottom !absolute w-full left-0 bottom-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 165">
            <path
              fillOpacity="1"
              d="M0,160L120,154.7C240,149,480,139,720,128C960,117,1200,107,1320,101.3L1440,96L1440,320L1320,320C1200,320,960,320,720,320C480,320,240,320,120,320L0,320Z"
              fill="white"
            />
          </svg>
        </div>
      </section>
    </>
  );
};
